import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { Paths } from "../../Services/EndPoints";
import { fetcher } from "../../Utils/HelperFunctions";

// Full-page generic/500 fallback — redesign mockup 6.
// Uses window.location (not useNavigate) so it works even if the crash is
// above the Router. No footer per the mock.
export const ErrorFallback = ({ onReset }) => {
  const { t } = useTranslation();
  const storeName =
    useSelector((s) => s.storeDetails?.storeName) || "The Crafts Archive";
  // Shared SWR key/fetcher → reuses the app-wide about_me cache, no extra request.
  const { data: store } = useSWR(Paths.about_me, fetcher);

  const onWhatsApp = () =>
    window.open(
      store?.mobileNumber
        ? `https://wa.me/91${store.mobileNumber}`
        : "https://wa.me/",
      "_blank",
    );

  return (
    <div className="min-h-screen bg-white font-geomanist flex flex-col">
      <div className="flex items-center gap-[10px] px-[14px] py-[13px] border-b border-wc-border">
        <span className="text-[15px] font-[500] text-ink-900 leading-none">
          {storeName}
        </span>
      </div>

      <div className="flex-grow px-5 pt-[34px] text-center">
        <div className="mx-auto w-[78px] h-[78px] rounded-full bg-[#FFF1EC] grid place-items-center mb-4">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-[38px] h-[38px] text-coral-ink"
          >
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2 className="text-[18px] font-[500] text-ink-900 mt-1 mb-1.5">
          {t("errorPage.title")}
        </h2>
        <p className="text-[12.5px] text-ink-500 leading-[1.5] max-w-[240px] mx-auto">
          <Trans
            i18nKey="errorPage.sub"
            components={[<b className="font-[500] text-ink-900" />]}
          />
        </p>

        <div className="flex flex-col gap-[9px] pt-4">
          <button
            onClick={() => (onReset ? onReset() : window.location.reload())}
            className="w-full bg-s-primary text-white font-[500] text-[13.5px] py-[13px] rounded-[12px]"
          >
            {t("errorPage.tryAgain")}
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="w-full bg-white text-s-primary font-[500] text-[13.5px] py-[13px] rounded-[12px] border-[1.5px] border-s-primary"
          >
            {t("errorPage.goToHome")}
          </button>
          <button
            onClick={onWhatsApp}
            className="w-full bg-[#25D366] text-white font-[500] text-[13.5px] py-[13px] rounded-[12px] flex items-center justify-center gap-2"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-[17px] h-[17px]"
            >
              <path d="M12 2a10 10 0 0 0-8.6 15l-1.4 5 5.1-1.3A10 10 0 1 0 12 2zm5.3 14.2c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7s-3.8-3.2-3.9-3.4c-.1-.2-1-1.3-1-2.5s.6-1.8.9-2c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.3 0 .5l-.4.5c-.2.2-.3.4-.1.6.1.3.7 1.1 1.4 1.7.9.8 1.6 1 1.9 1.2.2.1.4.1.5-.1l.7-.9c.2-.2.3-.2.6-.1l1.9.9c.3.1.4.2.5.3.1.2.1.6-.1 1.1z" />
            </svg>
            {t("errorPage.askOnWhatsApp", {
              name: store?.firstName || t("errorPage.us"),
            })}
          </button>
        </div>
      </div>
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      ...this.state,
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onReset={() => window.location.reload()} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
